import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnvironmentToken } from '../../../app.config';
import {
  Content,
  StoreContentPayload,
  UpdateContentPayload,
} from '../../models/content.model';

/**
 * Announcement maintenance (R17). Root-provided on purpose: listing a shared
 * HTTP service in a route's `providers` lets Vite's chunk splitting resolve
 * it as `undefined` at runtime.
 */
@Injectable({ providedIn: 'root' })
export class ContentService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;

  #items = new BehaviorSubject<Content[]>([]);
  public items$ = this.#items.asObservable();
  public items = toSignal(this.items$, { initialValue: [] });

  getAll() {
    this.#http
      .get<{ data: Content[] }>(`${this.#apiUrl}/api/admin/contents`)
      .subscribe({
        next: (response) => this.#items.next(response.data ?? []),
        error: (e) => console.log(e),
      });
  }

  getById(id: number): Observable<{ data: Content }> {
    return this.#http.get<{ data: Content }>(
      `${this.#apiUrl}/api/admin/contents/${id}`,
    );
  }

  create(payload: StoreContentPayload): Observable<{ data: Content }> {
    return this.#http.post<{ data: Content }>(
      `${this.#apiUrl}/api/admin/contents`,
      payload,
    );
  }

  update(
    id: number,
    payload: UpdateContentPayload,
  ): Observable<{ data: Content }> {
    return this.#http.patch<{ data: Content }>(
      `${this.#apiUrl}/api/admin/contents/${id}`,
      payload,
    );
  }

  delete(id: number): Observable<unknown> {
    return this.#http.delete(`${this.#apiUrl}/api/admin/contents/${id}`);
  }

  /**
   * The image collection is single-file on the API: a new upload replaces
   * the previous one, so there is no "remove first" step.
   */
  uploadImage(id: number, file: File): Observable<unknown> {
    const body = new FormData();
    body.append('collection', 'content-image');
    body.append('files[]', file);

    return this.#http.post(
      `${this.#apiUrl}/api/admin/contents/${id}/media`,
      body,
    );
  }

  deleteImage(id: number, mediaId: number): Observable<unknown> {
    return this.#http.delete(
      `${this.#apiUrl}/api/admin/contents/${id}/media/${mediaId}`,
    );
  }
}
